import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DollarSign, UserPlus, Crown, Trash2, RefreshCw,
  IndianRupee, Search, Eye, Calendar, Hash, TrendingUp, Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DATE_RANGES = [
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 6 months', days: 180 },
  { label: 'Last 1 year', days: 365 },
  { label: 'All time', days: null },
];

const SuperAdminDashboard = () => {
  const { token } = useAuth();
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState('all'); // 'all' | '30' | '180' | '365'
  const [showAddUser, setShowAddUser] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'general' });
  const [loading, setLoading] = useState(true);
  const [usdToInr, setUsdToInr] = useState(null);
  const [rateLoading, setRateLoading] = useState(true);

  const fetchRate = useCallback(async () => {
    setRateLoading(true);
    try {
      const res = await fetch('https://open.er-api.com/v6/latest/USD');
      const data = await res.json();
      if (data?.rates?.INR) setUsdToInr(data.rates.INR);
    } catch { setUsdToInr(null); }
    finally { setRateLoading(false); }
  }, []);

  useEffect(() => { loadPayments(); fetchRate(); }, [fetchRate]);

  const loadPayments = async () => {
    try {
      const res = await axios.get(`${API}/super-admin/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayments(res.data.payments);
    } catch { toast.error('Failed to load payments'); }
    finally { setLoading(false); }
  };

  const formatDate = (ts) =>
    new Date(ts).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const toUsd = (paise) => usdToInr ? `$${(paise / 100 / usdToInr).toFixed(2)}` : '—';

  // Filter by date range and search
  const filtered = useMemo(() => {
    let list = payments;

    // Date filter
    if (dateRange !== 'all') {
      const cutoff = new Date(Date.now() - parseInt(dateRange) * 86400000);
      list = list.filter(p => new Date(p.created_at) >= cutoff);
    }

    // Search: email, id, order_id, type, date string
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(p =>
        p.user_email?.toLowerCase().includes(q) ||
        p.id?.toLowerCase().includes(q) ||
        p.order_id?.toLowerCase().includes(q) ||
        p.type?.toLowerCase().includes(q) ||
        formatDate(p.created_at).toLowerCase().includes(q)
      );
    }

    return list;
  }, [payments, search, dateRange]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/super-admin/add-user`, newUser, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('User added successfully');
      setShowAddUser(false);
      setNewUser({ email: '', password: '', role: 'general' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add user'); }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Remove this payment from the dashboard?')) return;
    try {
      await axios.delete(`${API}/super-admin/payments/${paymentId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Payment removed');
      setPayments(prev => prev.filter(p => p.id !== paymentId));
      if (selectedPayment?.id === paymentId) setSelectedPayment(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  // Stats computed from filtered list
  const totalInr = filtered.reduce((s, p) => s + p.amount, 0) / 100;
  const totalUsd = usdToInr ? `$${(totalInr / usdToInr).toFixed(2)}` : null;
  const premiumCount = filtered.filter(p => p.type === 'premium').length;
  const promoCount = filtered.filter(p => p.type === 'promotion').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">Super Admin</h1>
              <p className="text-gray-500 dark:text-slate-400 text-sm">Payment management & user control</p>
            </div>
            <Button onClick={() => setShowAddUser(true)} className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white">
              <UserPlus className="w-4 h-4 mr-2" /> Add User
            </Button>
          </div>

          {/* Live rate banner */}
          <div className="mb-6 flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-sm text-blue-800 dark:text-blue-300">
            <RefreshCw className={`w-4 h-4 flex-shrink-0 ${rateLoading ? 'animate-spin' : ''}`} />
            {rateLoading ? 'Fetching live rate…' : usdToInr ? `Live rate: 1 USD = ₹${usdToInr.toFixed(2)} INR` : 'Exchange rate unavailable'}
            <button onClick={fetchRate} className="ml-auto text-xs underline opacity-70 hover:opacity-100">Refresh</button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Revenue', value: `₹${totalInr.toFixed(2)}`, sub: totalUsd ? `≈ ${totalUsd}` : null, color: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800', icon: <IndianRupee className="w-4 h-4 text-green-600" />, text: 'text-green-900 dark:text-green-200' },
              { label: 'Transactions', value: filtered.length, color: 'from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-200 dark:border-blue-800', icon: <TrendingUp className="w-4 h-4 text-blue-600" />, text: 'text-blue-900 dark:text-blue-200' },
              { label: 'Premium Sales', value: premiumCount, color: 'from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800', icon: <Crown className="w-4 h-4 text-purple-600" />, text: 'text-purple-900 dark:text-purple-200' },
              { label: 'Promotions', value: promoCount, color: 'from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-yellow-200 dark:border-yellow-800', icon: <DollarSign className="w-4 h-4 text-yellow-600" />, text: 'text-yellow-900 dark:text-yellow-200' },
            ].map(({ label, value, sub, color, icon, text }) => (
              <div key={label} className={`bg-gradient-to-br ${color} rounded-2xl border p-5`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-medium text-gray-600 dark:text-slate-400">{label}</p>
                  {icon}
                </div>
                <p className={`text-2xl font-bold ${text}`}>{value}</p>
                {sub && <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{sub}</p>}
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
            {/* Table header with filters */}
            <div className="p-5 border-b border-gray-200 dark:border-slate-700">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Payment Transactions</h2>
                <span className="text-sm text-gray-500 dark:text-slate-400">{filtered.length} of {payments.length} records</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by email, transaction ID, order ID, type…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9 text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-slate-400"
                  />
                </div>
                {/* Date range filter */}
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden">
                    {[
                      { key: '30', label: '30d' },
                      { key: '180', label: '6mo' },
                      { key: '365', label: '1yr' },
                      { key: 'all', label: 'All' },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => setDateRange(key)}
                        className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                          dateRange === key
                            ? 'bg-cyan-500 text-white'
                            : 'text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-slate-700/60">
                  <tr>
                    {['User', 'Type', 'INR', 'USD', 'Date', 'Status', ''].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {filtered.length > 0 ? filtered.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/40 transition-colors">
                      <td className="px-5 py-3.5 text-sm text-gray-900 dark:text-white max-w-[200px] truncate">{p.user_email}</td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          p.type === 'premium'
                            ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300'
                            : 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                        }`}>
                          {p.type === 'premium' ? '👑 Premium' : '⚡ Promo'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-sm font-semibold text-gray-900 dark:text-white">₹{(p.amount / 100).toFixed(2)}</td>
                      <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-slate-400">{toUsd(p.amount)}</td>
                      <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-slate-400 whitespace-nowrap">{formatDate(p.created_at)}</td>
                      <td className="px-5 py-3.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">✓ Done</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setSelectedPayment(p)} className="p-1.5 rounded-lg text-cyan-500 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition" title="View details">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeletePayment(p.id)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-16 text-center text-gray-400 dark:text-slate-500">
                        {search || dateRange !== 'all' ? 'No results match your filters' : 'No payments yet'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Detail Dialog */}
      <Dialog open={!!selectedPayment} onOpenChange={() => setSelectedPayment(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" /> Transaction Details
            </DialogTitle>
          </DialogHeader>
          {selectedPayment && (
            <div className="space-y-4">
              <div className="text-center p-5 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800">
                <p className="text-3xl font-bold text-green-800 dark:text-green-300">₹{(selectedPayment.amount / 100).toFixed(2)}</p>
                {usdToInr && <p className="text-sm text-green-600 dark:text-green-400 mt-1">≈ {toUsd(selectedPayment.amount)} USD</p>}
                <span className={`inline-flex items-center mt-2 px-3 py-1 rounded-full text-xs font-medium ${
                  selectedPayment.type === 'premium'
                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                }`}>
                  {selectedPayment.type === 'premium' ? '👑 Premium Subscription' : '⚡ Post Promotion'}
                </span>
              </div>
              <div className="space-y-2">
                {[
                  { icon: <Hash className="w-4 h-4" />, label: 'Transaction ID', value: selectedPayment.id, mono: true },
                  { icon: <Hash className="w-4 h-4" />, label: 'Order ID', value: selectedPayment.order_id || '—', mono: true },
                  { icon: <DollarSign className="w-4 h-4" />, label: 'User', value: selectedPayment.user_email },
                  { icon: <Calendar className="w-4 h-4" />, label: 'Date', value: formatDate(selectedPayment.created_at) },
                  { icon: <TrendingUp className="w-4 h-4" />, label: 'Status', value: 'Completed ✓' },
                ].map(({ icon, label, value, mono }) => (
                  <div key={label} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-xl">
                    <span className="text-gray-400 dark:text-slate-500 mt-0.5 flex-shrink-0">{icon}</span>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 dark:text-slate-400 mb-0.5">{label}</p>
                      <p className={`text-sm text-gray-900 dark:text-white break-all ${mono ? 'font-mono text-xs' : 'font-medium'}`}>{value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" className="w-full border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20" onClick={() => handleDeletePayment(selectedPayment.id)}>
                <Trash2 className="w-4 h-4 mr-2" /> Remove from Dashboard
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div>
              <Label className="dark:text-slate-300 text-sm">Email</Label>
              <Input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} required className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
            </div>
            <div>
              <Label className="dark:text-slate-300 text-sm">Password</Label>
              <Input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} required className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
            </div>
            <div>
              <Label className="dark:text-slate-300 text-sm">Role</Label>
              <Select value={newUser.role} onValueChange={v => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white">
              Add User
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;